/* eslint-disable @typescript-eslint/no-non-null-assertion */
import moment from 'moment';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Component, Input, OnInit } from '@angular/core';
import {
  IDateRange,
  IMachineTotals,
  ILabel,
  GraphPeriodType,
  IMachineTotalJobs,
  IMachineJobsByLabelFilter,
} from 'app/models';
import { Constants } from '@config/constants';
import { MessageService } from 'app/services/message/message.service';
import { saveAs } from 'file-saver';
import { AuthenticationTokenStorageService, DataWorkByLabelService } from 'app/services';

@Component({
  selector: 'app-departments-component',
  templateUrl: './departments.component.html',
  styleUrls: ['./departments.component.scss'],
})
export class DepartmentsComponent implements OnInit {
  @Input()
  listOfDepartmentsFromApi: Array<ILabel> = [];

  customerId: number | null = null;

  dateRange: IDateRange | null = null;

  zoneId?: string;

  selectedPeriod = GraphPeriodType.DAILY;

  selectedDepartment: string | undefined;

  departmentTotalJobsList: Array<IMachineTotalJobs> = [];

  selectedDepartmentTotals$: Observable<IMachineTotals> | null = null;

  constructor(
    private readonly dataWorkByLabelService: DataWorkByLabelService,
    private readonly constants: Constants,
    private readonly messageService: MessageService,
    private readonly storageService: AuthenticationTokenStorageService
  ) {}

  ngOnInit(): void {
    const { customerId } = this.storageService.getLoggedUser();
    this.customerId = customerId;
  }

  onDataWorkCsvDownloadClicked() {
    if(this.selectedDepartment && this.dateRange) {
      const queryParams =
      `customerId=${this.customerId}`
      +`&from=${moment(this.dateRange.startDate).format('YYYY-MM-DDTHH:mm:ss')}`
      +`&to=${moment(this.dateRange.endDate).format('YYYY-MM-DDTHH:mm:ss')}`
      +`&labelCode=${this.selectedDepartment}`
      +`&labelType=${this.constants.DEPARTMENT_LABEL_TYPE}`;
      this.dataWorkByLabelService.downloadDataWorkCSVByLabel(queryParams).subscribe((fileBlob: Blob) => {
        saveAs(fileBlob, `${this.selectedDepartment}-data-work.csv`);
      });
    }
  }

  handleSelectedDepartmentChange(selectedDepartment?: string): void {
    if (selectedDepartment != null) {
      this.selectedDepartment =
        this.listOfDepartmentsFromApi.find(
          (department) => department.code === selectedDepartment
        )?.code;

      if (this.selectedDepartment) {
        this.updateDepartmentTotalJobsList({
          customerId: this.customerId!,
          labelCode: this.selectedDepartment,
        });

        this.getSelectedDepartmentTotals({
          customerId: this.customerId!,
          labelCode: this.selectedDepartment,
        });
      }
    } else {
      this.selectedDepartment = undefined;
    }
  }

  handleFilterByDateChanged(dateRange: IDateRange): void {
    this.dateRange = dateRange;
    this.zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (this.selectedDepartment) {
      this.updateDepartmentTotalJobsList({
        customerId: this.customerId!,
        labelCode: this.selectedDepartment,
        dateRange,
      });

      this.getSelectedDepartmentTotals({
        customerId: this.customerId!,
        labelCode: this.selectedDepartment,
        dateRange,
      });
    }
  }

  public isIntervalInThreshold( dateRange: IDateRange | null): boolean {
     if ( !dateRange ) {
      return false;
     }
     const result = dateRange.endDate.diff(dateRange.startDate, 'days') < this.constants.INTERVAL_THRESHOLD_DAYS_TO_SHOW_JOB_DETAILS;
     return result;
  }

  private updateDepartmentTotalJobsList({
    customerId,
    dateRange: { startDate, endDate } = {
      startDate: moment().startOf('day'),
      endDate: moment().endOf('day'),
    },
    labelCode,
  }: IMachineJobsByLabelFilter): void {
    if (! this.isIntervalInThreshold( { startDate, endDate } )) {
      this.departmentTotalJobsList = [];
      this.messageService.showDirectSimpleMessage(` If Job details exists they are not displayed due to too long a date interval `);
      return;
    }
    this.dataWorkByLabelService.getTotalJobsByLabel({
        customerId,
        dateRange: { startDate, endDate },
        labelCode: labelCode!,
        labelType: this.constants.DEPARTMENT_LABEL_TYPE,
      })
      .pipe(
        take(1),
        map((res) =>
          res.map(
            ({
              fileName,
              machineId,
              totalTime,
              dateTimeWork,
              cutMetersAsString,
              totalTimeAsHHmmss,
              creaseMetersAsString,
              physicalIncoherent,
            }) => ({
              fileName,
              machineId,
              totalTime,
              dateTimeWork,
              cutMetersAsString,
              totalTimeAsHHmmss,
              creaseMetersAsString,
              average: parseFloat(
                (
                  (parseFloat(cutMetersAsString) +
                    parseFloat(creaseMetersAsString)) /
                  (totalTime / 3600)
                ).toString()
              ).toFixed(2),
              physicalIncoherent,
            })
          )
        )
      )
      .subscribe(
        (data) => (this.departmentTotalJobsList = data),
        () => console.error
      );
  }

  private getSelectedDepartmentTotals({
    customerId,
    dateRange: { startDate, endDate } = {
      startDate: moment().startOf('day'),
      endDate: moment().endOf('day'),
    },
    labelCode,
  }: IMachineJobsByLabelFilter): void {
    this.selectedDepartmentTotals$ = this.dataWorkByLabelService.getTotalsByLabel({
      customerId,
      dateRange: { startDate, endDate },
      labelCode,
      labelType: this.constants.DEPARTMENT_LABEL_TYPE,
    });
  }

}
